import { useMemo, useState } from 'react'
import { AlertTriangle, ArrowRight, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AxisValueSwatch } from '../components/axis-visuals'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import { pushStudioRoute, studioRoutePath } from '@/lib/studio-navigation'
import { useStudioStore } from '@/store/studio-store'
import { ReadinessItemRow } from '../../generation-readiness/readiness-item-row'
import { translateAxisResolution } from '../../generation-readiness/translate-gaps'
import type { ReadinessItem } from '../../generation-readiness/translate-gaps'
import { radiusOptionLabel, type DesignAxesCopy } from './axes-copy'
import {
  BASE_COLOR_OPTIONS,
  CHART_OPTIONS,
  FONT_OPTIONS,
  RADIUS_OPTIONS,
  STYLE_OPTIONS,
  THEME_OPTIONS,
  type LabeledOption,
} from '../lib/design-system-options'
import type { Advice, AxisName, AxisResolution, SourceDerivedDesignSystem } from './types'
import { describeDesignSystem } from '../provenance/design-decisions'
import { UndecidedValuesPanel } from '../provenance/undecided-values-panel'
import { DEFAULT_DESIGN_SYSTEM_STATE } from '../lib/design-system-options'
import type { DesignSystemAxes } from '@cogo/design-presets'
import type { DesignSystemState } from '../lib/design-system-options'

/**
 * The axes the preview actually paints with.
 *
 * `design-system-preview` renders from the store's `designSystem`, so that is
 * the creator's effective choice. `derived` is the SOURCE's reading of the
 * axes and does not move when the creator picks a value — measured in a real
 * browser: choosing Lyra moved the store and repainted the preview while every
 * `derived` row stayed a gap. Reading `derived` here asked "is the source's
 * value in effect", which is not a question anyone has.
 */
function effectiveAxes(designSystem: DesignSystemState): DesignSystemAxes {
  return { ...DEFAULT_DESIGN_SYSTEM_STATE, ...designSystem } as DesignSystemAxes
}

/**
 * `translateAxisResolution` (generation-readiness/translate-gaps.ts) always
 * points a gap axis's suggestion at THIS screen —
 * `{ label: '디자인시스템에서 정하기', navigateTo: '/studio/design-system' }` —
 * because that translation layer has exactly one destination in mind for
 * "go decide this axis" and no notion of who is calling it (by design: it is
 * a pure, screen-agnostic translation layer with no callers to special-case,
 * see that file's own doc comment). That destination is sound advice from
 * the OTHER place a `ReadinessItem` built from this function is shown (the
 * pre-generation readiness panel, reached from chat) — but rendered HERE, on
 * `/studio/design-system` itself, the identical suggestion becomes a link
 * from this screen back to this screen. `studioRoutePath('design-system')`
 * (not a re-typed literal) is compared against so this stays correct if the
 * canonical path ever changes.
 */
const DESIGN_SYSTEM_ROUTE_PATH = studioRoutePath('design-system')

/**
 * Strips a gap item's suggestion when it is this exact self-link — the
 * whole suggestion, not just `navigateTo` (kept `label` with no destination
 * would read as an instruction with nothing to click, no more honest than
 * the link it replaces). Nothing is lost: the axis's own select+Choose
 * control, two lines below wherever this item renders, is already the real,
 * immediate way to act on the gap — a "go set it in the design system"
 * prompt on the design system screen is pure redundancy with the control
 * sitting right next to it, not a missing piece of information.
 */
function suppressSelfLinkSuggestion(item: ReadinessItem): ReadinessItem {
  if (item.suggestion?.navigateTo !== DESIGN_SYSTEM_ROUTE_PATH) return item
  return {
    kind: item.kind,
    title: item.title,
    explanation: item.explanation,
    machineCode: item.machineCode,
    evidenceRefs: item.evidenceRefs,
    // suggestion intentionally omitted — see doc comment above.
  }
}

const AXIS_ORDER: AxisName[] = ['style', 'baseColor', 'theme', 'chart', 'radius', 'font']

const OPTIONS_BY_AXIS: Record<AxisName, LabeledOption[]> = {
  style: STYLE_OPTIONS,
  baseColor: BASE_COLOR_OPTIONS,
  theme: THEME_OPTIONS,
  chart: CHART_OPTIONS,
  radius: RADIUS_OPTIONS,
  font: FONT_OPTIONS,
}

export interface AxisResolutionListProps {
  derived: SourceDerivedDesignSystem
  onChoose: (axis: AxisName, value: string) => void
}

/**
 * The 6 axes a source-derived design system resolves to, always all shown —
 * a gap is a state this screen names ("you decide this"), never a failure it
 * hides. Every row carries `explanation`, written so a person can read why
 * the axis holds its current value — or why it doesn't have one yet —
 * without knowing what a Figma variable is.
 *
 * `Advice.severity` has no blocking value ('note' | 'caution' only, see
 * `./types`) and nothing here ever disables a control over it — a `caution`
 * gets a different icon, never a different (destructive) colour. Same tone
 * rule as `inventoryToneClassName` a few files up in
 * `agentic/agentic-design-system-page.tsx`: attention-red is reserved for a
 * genuine read failure, not for "you haven't decided yet" or "here's a
 * heads up".
 *
 * Task 5 (ontology-ux-contract cycle): a `gap` axis (`origin:'gap'`) no
 * longer renders its own bespoke "label + 정해지지 않음 + explanation" markup —
 * it goes through `translateAxisResolution` into the same `ReadinessItem`
 * shape the generation-readiness panel (`generation-readiness/readiness-panel.tsx`)
 * shows for a blocking/advisory item, rendered by the same
 * `ReadinessItemRow`. This is the point of the exercise: a gap reads the
 * same whether you meet it here or in the pre-generation check screen. Only
 * the gap's own header+explanation is replaced — the axis's `advice` list
 * (`AdviceRow` below) and the interactive select+Choose control are
 * unchanged and still render for every axis, gap or not. One adjustment on
 * the way in: `suppressSelfLinkSuggestion` (above) drops the item's
 * suggestion when it would otherwise link this screen back to itself.
 */
export function AxisResolutionList({ derived, onChoose }: AxisResolutionListProps) {
  // Measured against the configuration the preview actually paints with, so a
  // control whose choice reaches nothing can say so instead of showing a value
  // that is not in force.
  const designSystem = useStudioStore((state) => state.designSystem)
  const axes = effectiveAxes(designSystem as DesignSystemState)
  // ONE sweep of the producer, three views of it. `measureMovers` re-resolves
  // the theme once per option per axis, and this component used to pay for two
  // such sweeps per render (and would now pay for three, since the decisions
  // themselves finally have a consumer). Memoised on the six axis VALUES
  // rather than on `axes`, which `effectiveAxes` rebuilds every render.
  const { decisions, axisEffect: effect, scope } = useMemo(
    () => describeDesignSystem(axes, DEFAULT_DESIGN_SYSTEM_STATE as DesignSystemAxes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [axes.style, axes.baseColor, axes.theme, axes.chart, axes.radius, axes.font],
  )
  const { strings } = useStudioLocale()
  const copy = strings.designSystemAxes
  const workbench = strings.designSystem.workbench
  return (
    <section
      data-testid="axis-resolution-list"
      className="rounded-lg border border-border bg-card px-4 py-3"
    >
      <header>
        <h2 className="text-sm font-semibold text-foreground">
          {workbench.sourceTitle}
        </h2>
        <SourceLine source={derived.source} />
      </header>
      {/* What these controls cover. The component half carries both numbers
          because they come from two DIFFERENT producers — what the paint path
          resolves, and what `themes/default-theme.json` declares — so a drift
          between them shows up here.

          The comment that stood here said this screen "resolves a handful of
          components while the platform's canonical theme declares many more".
          Measured 2026-08-26 that is no longer so: `axesToThemeV2` seeds its
          atom map from the canonical theme, and the strip renders 47 · 47.
          The pair is the instrument that would show the gap REOPENING, not a
          claim that one exists today. See `summarizeScope`'s doc comment. */}
      <div
        data-testid="design-scope-summary"
        className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
      >
        <span>
          <span className="font-semibold text-foreground">{copy.scopeTheme}</span>{' '}
          {copy.scopeThemeValues(scope.theme.values)}
        </span>
        <span>
          <span className="font-semibold text-foreground">{copy.scopeComponent}</span>{' '}
          {copy.scopeComponentReach(
            scope.component.reachableHere.length,
            scope.component.declaredBySystem,
          )}
        </span>
      </div>

      <div className="mt-3 flex flex-col gap-3">
        {AXIS_ORDER.map((axis) => (
          <AxisRow
            key={axis}
            resolution={derived.axes[axis]}
            onChoose={onChoose}
            copy={copy}
            effect={effect[axis]}
          />
        ))}
      </div>

      {/* (c) of the operator's three facts — what is in force because this
          project never chose it. Rendered BELOW the controls deliberately: it
          is the consequence of the six decisions above, and a creator reads it
          after seeing what they can change, not before. */}
      <UndecidedValuesPanel decisions={decisions} copy={copy} />
    </section>
  )
}

function SourceLine({ source }: { source: SourceDerivedDesignSystem['source'] }) {
  const setStudioRoute = useStudioStore((s) => s.setStudioRoute)
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.workbench
  if (!source.label) {
    return (
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="text-[13px] leading-5 text-muted-foreground">
          {copy.sourceEmptyBody}
        </p>
        {/* FINDING-006 (DS ↔ OpenPencil bridge): the empty state named the
            gap but offered no way to act on it — the OpenPencil/Figma import
            lane existed with no path from this screen. This is a navigation
            bridge, not a library browser: the browsable admitted-library list
            lives in ../verified-libraries/verified-libraries-section.tsx,
            rendered by SystemsPanel below this list, reading the canonical
            client-readable registry that FINDING-006 called for. */}
        <button
          type="button"
          data-testid="design-source-import-link"
          onClick={() => {
            setStudioRoute('import')
            pushStudioRoute('import')
          }}
          className="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-[13px] font-semibold text-foreground transition-colors hover:bg-muted"
        >
          {copy.sourceImportCta}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    )
  }
  return (
    <p className="mt-1 text-xs leading-5 text-muted-foreground">
      {copy.sourceReadPrefix}
      <span className="font-medium text-foreground">{source.label}</span>
      <span>
        {' '}
        {(source.variableCount === 1
          ? copy.sourceVariableCountOne
          : copy.sourceVariableCount
        ).replace('{count}', String(source.variableCount))}
      </span>
    </p>
  )
}

function AxisRow({
  resolution,
  onChoose,
  copy,
  effect,
}: {
  resolution: AxisResolution
  onChoose: (axis: AxisName, value: string) => void
  copy: DesignAxesCopy
  effect?: { decides: number; overriddenBy: AxisName | null }
}) {
  const { strings } = useStudioLocale()
  const workbench = strings.designSystem.workbench
  const options = OPTIONS_BY_AXIS[resolution.axis]
  // A gap axis's advice (see advise.ts) carries the same default this axis
  // would get in "auto mode" (Task 5) — surfacing it here just pre-fills
  // the picker with it, it does not apply it. Nothing commits until
  // "Choose" is clicked, and clicking it is always possible: there is no
  // state in which this control is disabled.
  const suggestedValue = resolution.advice.find((item) => item.suggestion)?.suggestion?.value
  const [pending, setPending] = useState(
    resolution.value ?? suggestedValue ?? options[0]?.value ?? '',
  )
  // null for every non-gap origin (from-source/chosen-*) — translateAxisResolution
  // only produces an item when origin==='gap'. See module doc above.
  const gapItem = translateAxisResolution(resolution, copy)

  return (
    <div
      data-testid="axis-resolution"
      className="rounded-md border border-border bg-background px-3 py-3"
    >
      {gapItem ? (
        <ul className="flex flex-col gap-2">
          <ReadinessItemRow item={suppressSelfLinkSuggestion(gapItem)} />
        </ul>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-sm font-semibold text-foreground">
              {copy.axisLabels[resolution.axis]}
            </span>
            <span className="text-sm text-muted-foreground">
              {resolution.value ?? copy.notDecidedValue}
            </span>
          </div>

          <p className="mt-1 text-xs leading-5 text-muted-foreground">{resolution.explanation}</p>

        </>
      )}

      {/* Rendered outside the gap/non-gap split on purpose. Whether the value
          came from the source or from the creator, a control has to say what
          it is doing — and measured, the gap branch is the common one: of the
          four declared origins only `from-source` has a production producer,
          so a project whose source yields no axis values is all gaps.

          Both outcomes get words. `decides` was already computed and already
          passed in here, and was read ONLY in the negative branch, so six
          operable controls said what they changed only when the answer was
          "nothing". The positive case is the ordinary one and had no sentence
          at all. Mutually exclusive by construction: a control either decides
          something or it does not. */}
      {effect && effect.decides > 0 ? (
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {copy.decidesValues(effect.decides)}
        </p>
      ) : effect && effect.overriddenBy ? (
        <p className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-400">
          {copy.notInEffect} {copy.decidedInsteadBy(copy.axisLabels[effect.overriddenBy])}
        </p>
      ) : null}

      {resolution.advice.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1.5">
          {resolution.advice.map((advice, index) => (
            <AdviceRow key={index} advice={advice} />
          ))}
        </ul>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {/* The pending value's actual design (palette ramp / hue / chart
            strip / radius corner / font specimen) — the axis is design, so
            the picker shows design, not just its name. */}
        <AxisValueSwatch axis={resolution.axis} value={pending} />
        <select
          aria-label={workbench.axisValueSelectLabel.replace(
            '{axis}',
            copy.axisLabels[resolution.axis],
          )}
          value={pending}
          onChange={(event) => setPending(event.target.value)}
          className="h-10 rounded-md border border-border bg-background px-2 text-xs text-foreground"
        >
          {/* Only the radius axis's labels are copy — every other axis lists
              proper names (`Zinc`, `Noto Sans`) that stay Latin in every
              locale. Guarded on the axis rather than left to the lookup so a
              preset named `default` on some other axis can never be renamed
              by the radius table. */}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {resolution.axis === 'radius' ? radiusOptionLabel(option, copy) : option.label}
            </option>
          ))}
        </select>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="min-h-10"
          onClick={() => onChoose(resolution.axis, pending)}
        >
          {workbench.chooseAxisValue}
        </Button>
      </div>
    </div>
  )
}

/**
 * `caution` is not an error: both severities render in the same
 * `text-muted-foreground` and differ only by icon. See the module doc
 * comment above for why.
 */
function AdviceRow({ advice }: { advice: Advice }) {
  const Icon = advice.severity === 'caution' ? AlertTriangle : Info
  return (
    <li className="flex items-start gap-2 rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <div>
        <p>{advice.message}</p>
        {advice.suggestion ? <p className="mt-0.5">{advice.suggestion.why}</p> : null}
      </div>
    </li>
  )
}
